import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons/faEllipsisH';

import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';

import { useLabelProviderSettingsContext } from '../../provider-settings-modal/context-provider';
import { useShipstationSettingContext } from '../../shipstation-settings-modal/context-provider';
import { useDeleteLabelProviderContext } from '../../delete-label-provider-modal/context-provider';
import getIsActive from '../../../../../components/data-driven-logo/get-is-active';
import { useSellerLabelProvider } from '../../../../../hooks/use-seller-label-provider';
import { useSellerLabelProviders } from '../../../../../hooks/use-seller-label-providers';
import messages from './messages';
import style from './provider-table.module.less';
import reconnectCarrier from '../../reconnect-account';
import { USPostalServiceId } from '../../../constants';

const preventDefault = (e) => e?.preventDefault();

const EditSettingsButton = ({ sellerLabelProviderId }) => {
  const sellerLabelProvider = useSellerLabelProvider(sellerLabelProviderId);
  const { name, isPrimaryAccount, labelProviderId } = sellerLabelProvider;
  const sellerLabelProvidersForProvider = useSellerLabelProviders(labelProviderId);
  const hasMultipleAccountsForProvider = sellerLabelProvidersForProvider.length > 1;
  const { onEditProvider } = useLabelProviderSettingsContext();
  const { onEditProviderSS, saveSetting } = useShipstationSettingContext();
  const { onDeleteProvider } = useDeleteLabelProviderContext();
  const isActive = getIsActive({ providerId: labelProviderId });
  const setAsDefault = useCallback(() => {
    if (!isPrimaryAccount) {
      saveSetting(sellerLabelProviderId, name, true);
    }
  }, [isPrimaryAccount, saveSetting, sellerLabelProviderId, name]);
  const editSetting = useCallback(
    () => onEditProvider(Number.parseInt(labelProviderId, 10), Number.parseInt(sellerLabelProviderId, 10)),
    [labelProviderId, onEditProvider, sellerLabelProviderId],
  );
  const hideIsPrimaryAccountSetting = true;
  const openEditNicknameModal = useCallback(
    () => onEditProviderSS(sellerLabelProviderId, hideIsPrimaryAccountSetting),
    [onEditProviderSS, sellerLabelProviderId, hideIsPrimaryAccountSetting],
  );
  const deleteCarrier = useCallback(() => onDeleteProvider(sellerLabelProvider), [onDeleteProvider, sellerLabelProvider]);

  const hasReconnect = [USPostalServiceId].includes(sellerLabelProvider.labelProviderId);
  const handleReconnectCarrier = () => {
    reconnectCarrier(sellerLabelProvider);
  };

  return (
    <DropdownMenu menuPosition='bottom-right'>
      <DropdownMenuToggler
        className={style['menu-toggle']}
        hideChevron
        onClick={preventDefault}
        openClassName={style['open-menu-toggle']}
        theme='link'
      >
        <div className={style['menu-toggle-contents']}>
          <FontAwesomeIcon icon={faEllipsisH} />
        </div>
      </DropdownMenuToggler>
      <DropdownMenuItem disabled={!isActive} onClick={editSetting}>
        <FormattedMessage {...messages.carrierSettings} />
      </DropdownMenuItem>
      {hasMultipleAccountsForProvider && (
        <DropdownMenuItem disabled={isPrimaryAccount} onClick={setAsDefault}>
          <FormattedMessage {...messages.setAsDefault} />
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={openEditNicknameModal}>
        <FormattedMessage {...messages.editNickname} />
      </DropdownMenuItem>
      {hasReconnect && (
        <DropdownMenuItem onClick={handleReconnectCarrier}>
          <FormattedMessage {...messages.reconnect} />
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={deleteCarrier} theme='danger-link'>
        <FormattedMessage {...messages.delete} />
      </DropdownMenuItem>
    </DropdownMenu>
  );
};

EditSettingsButton.propTypes = {
  sellerLabelProviderId: PropTypes.string.isRequired,
};

export default EditSettingsButton;
